import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import fetchPresignedUrl from '../api/fetchPresignedUrl';

export type PlaybackRate = 1.0 | 1.25 | 1.5 | 1.75 | 2.0;

interface UseAudioPlayerParams {
  recordingId: number;
  duration: number | null;
  presignedUrl: string;
}

interface UseAudioPlayerReturn {
  audioRef: RefObject<HTMLAudioElement | null>;
  audioSrc: string;
  currentTime: number;
  volume: number;
  isPlaying: boolean;
  playbackRate: PlaybackRate;
  seek: (progress: number) => void;
  setVolume: (volume: number) => void;
  togglePlayPause: () => void;
  setPlaybackRate: (rate: PlaybackRate) => void;
}

const useAudioPlayer = ({ recordingId, duration, presignedUrl }: UseAudioPlayerParams): UseAudioPlayerReturn => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolumeState] = useState(100);
  const [isPlaying, setIsPlaying] = useState(false);
  const [refreshedUrl, setRefreshedUrl] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [playbackRate, setPlaybackRateState] = useState<PlaybackRate>(1.0);

  const audioSrc = refreshedUrl ?? presignedUrl;

  const handleError = useCallback(async () => {
    if (isRefreshing) {
      return;
    }

    const audio = audioRef.current;
    if (audio === null) {
      return;
    }

    // 再生位置を保持
    const savedTime = currentTime;
    const wasPlaying = isPlaying;

    setIsRefreshing(true);
    try {
      const newUrl = await fetchPresignedUrl(recordingId);
      setRefreshedUrl(newUrl);

      // 新しいURLがロードされた後に再生位置を復元
      const handleLoaded = (): void => {
        audio.currentTime = savedTime;
        if (wasPlaying) {
          void audio.play();
        }
        audio.removeEventListener('loadedmetadata', handleLoaded);
      };
      audio.addEventListener('loadedmetadata', handleLoaded);
    } catch (error) {
      console.error('Failed to refresh presigned URL:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [recordingId, currentTime, isPlaying, isRefreshing]);

  // 外部システム（Audio DOM要素）との同期のためuseEffectを使用
  // 参照: https://ja.react.dev/learn/you-might-not-need-an-effect
  // 根拠: イベントリスナーの登録・解除は外部システムとの同期に該当する
  useEffect(() => {
    const audio = audioRef.current;
    if (audio === null) {
      return;
    }

    const handleTimeUpdate = (): void => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = (): void => {
      setIsPlaying(false);
    };

    const handleErrorEvent = (): void => {
      void handleError();
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleErrorEvent);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleErrorEvent);
    };
  }, [handleError]);

  // 外部システム（Audio API）との同期のため、playbackRateとaudioSrcが変わったときにAudio DOMと同期
  // 参照: https://ja.react.dev/learn/you-might-not-need-an-effect
  // 根拠: Audio DOM要素のプロパティ設定は外部システムとの同期に該当する
  useEffect(() => {
    const audio = audioRef.current;
    if (audio === null) {
      return;
    }
    audio.playbackRate = playbackRate;
  }, [playbackRate, audioSrc]);

  const seek = useCallback(
    (progress: number): void => {
      if (duration === null) {
        return;
      }
      const newTime = (progress / 100) * duration;
      setCurrentTime(newTime);
      if (audioRef.current !== null) {
        audioRef.current.currentTime = newTime;
      }
    },
    [duration]
  );

  const setVolume = useCallback((value: number): void => {
    setVolumeState(value);
    if (audioRef.current !== null) {
      audioRef.current.volume = value / 100;
    }
  }, []);

  const togglePlayPause = useCallback((): void => {
    if (audioRef.current === null) {
      return;
    }

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      void audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying]);

  const setPlaybackRate = useCallback((rate: PlaybackRate): void => {
    setPlaybackRateState(rate);
    const audio = audioRef.current;
    if (audio !== null) {
      audio.playbackRate = rate;
    }
  }, []);

  return {
    audioRef,
    audioSrc,
    currentTime,
    volume,
    isPlaying,
    playbackRate,
    seek,
    setVolume,
    togglePlayPause,
    setPlaybackRate,
  };
};

export default useAudioPlayer;
